// apps/web/src/pages/admin/AllJobsPage.tsx

import React, { useState, useEffect } from 'react';
import { trpc } from '@/trpc/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
// Added Alert, AlertTitle, AlertDescription, Terminal for the error boundary
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'; 
import { Briefcase, Search, ChevronLeft, ChevronRight, Loader2, InfoIcon, Eye, MapPin, DollarSign, CalendarDays, ArrowRight, Image, Terminal } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns'; // Removed parseISO
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Link } from 'react-router-dom';
import { JobStatus, District, PaymentStatus, BookingStatus } from '@logistics-platform/types'; 

// Type definition for a job returned by the API (expanded for details modal)
interface AdminJob {
    id: string;
    title: string;
    description?: string | null;
    pickupDistrict: District;
    pickupAddress: string;
    pickupContactName?: string | null;
    pickupContactPhone?: string | null;
    deliveryDistrict: District;
    deliveryAddress: string;
    deliveryContactName?: string | null;
    deliveryContactPhone?: string | null;
    priceAmount: number;
    loadCount: number;
    cargoWeight?: number | null;
    cargoUnit?: string | null;
    cargoWeightKg?: number | null;
    specialRequirements?: string | null;
    preferredPickupDate?: string | null;
    preferredDeliveryDate?: string | null;
    status: JobStatus;
    createdAt: string;
    updatedAt: string;
    client: {
        id: string; 
        email: string;
        profile: {
            fullName: string;
        } | null;
    } | null;
    bookings: {
      id: string;
      status: BookingStatus;
      bookingFee: number;
      transporterId: string; 
      transporter: {
        id: string; 
        profile: { fullName: string } | null;
        email: string;
      } | null;
      vehicle: {
        id: string;
        make: string;
        model: string;
        licensePlate: string;
        verified: boolean;
      } | null;
    }[];
    payment?: {
      id: string;
      totalAmount: number;
      commissionAmount: number;
      transporterAmount: number;
      status: PaymentStatus;
      paychanguRef?: string | null;
      createdAt: string;
    } | null;
    images: { filePath: string }[];
}

const AllJobsPage: React.FC = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedJobStatus, setSelectedJobStatus] = useState<JobStatus | undefined>(undefined);
    const [selectedPickupDistrict, setSelectedPickupDistrict] = useState<District | undefined>(undefined);
    const [selectedDeliveryDistrict, setSelectedDeliveryDistrict] = useState<District | undefined>(undefined);
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    const { data, isLoading, error } = trpc.admin.getAllJobs.useQuery({
        page,
        pageSize,
        search: debouncedSearchQuery,
        jobStatus: selectedJobStatus,
        pickupDistrict: selectedPickupDistrict,
        deliveryDistrict: selectedDeliveryDistrict,
        orderBy: 'createdAt',
        orderDirection: 'desc',
    });

    const jobs = data?.jobs || [];
    const totalJobs = data?.totalCount || 0;
    const totalPages = Math.ceil(totalJobs / pageSize);

    const [viewingJobId, setViewingJobId] = useState<string | null>(null); // State for fetching full job data
    const { data: fullJobData, isLoading: isLoadingFullJob, error: fullJobError } = trpc.admin.getJobById.useQuery({
      jobId: viewingJobId!
    }, {
      enabled: !!viewingJobId,
      staleTime: 0, 
    });
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewingJob, setViewingJob] = useState<AdminJob | null>(null); // State for modal display

    // Initialize viewingJob when fullJobData is fetched
    useEffect(() => {
      if (fullJobData) {
        setViewingJob(fullJobData);
      }
    }, [fullJobData]);


    const handleViewClick = (jobId: string) => {
        setViewingJobId(jobId);
        setIsViewModalOpen(true);
    };


    if (error) {
        return (
            <div className="container mx-auto py-12 px-4 max-w-7xl">
                <Alert variant="destructive">
                    <Terminal className="h-4 w-4" />
                    <AlertTitle>Error Loading Jobs</AlertTitle>
                    <AlertDescription>{error.message}</AlertDescription>
                </Alert>
            </div>
        );
    }
    
    return (
        <div className="container mx-auto py-6 px-2 sm:px-4 max-w-full animate-fade-in">
            <Card className="glass">
                <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="flex items-center gap-2 text-foreground text-lg sm:text-2xl"><Briefcase className="h-5 w-5 sm:h-6 sm:w-6 text-primary"/>View All Jobs</CardTitle>
                    <CardDescription className="text-xs sm:text-sm">Access and review all transportation jobs posted on the platform.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6 p-2 sm:p-6">
                    {/* Search and Filters */}
                    <div className="flex flex-col md:flex-row gap-3 sm:gap-4 flex-wrap">
                        <Input 
                            placeholder="Search by title, description, or address..." 
                            value={searchQuery} 
                            onChange={(e) => setSearchQuery(e.target.value)} 
                            className="w-full md:flex-1 bg-input text-foreground h-9 sm:h-10 text-sm"
                        />
                        <Select value={selectedJobStatus} onValueChange={(value: JobStatus) => setSelectedJobStatus(value)}>
                            <SelectTrigger className="w-full md:w-[180px] bg-select text-foreground h-9 sm:h-10 text-sm">
                                <SelectValue placeholder="Filter by Status" />
                            </SelectTrigger>
                            <SelectContent className="bg-popover text-popover-foreground">
                                <SelectItem value={undefined}>All Statuses</SelectItem>
                                {Object.values(JobStatus).map(status => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={selectedPickupDistrict} onValueChange={(value: District) => setSelectedPickupDistrict(value)}>
                            <SelectTrigger className="w-full md:w-[180px] bg-select text-foreground h-9 sm:h-10 text-sm">
                                <SelectValue placeholder="Pickup District" />
                            </SelectTrigger>
                            <SelectContent className="bg-popover text-popover-foreground">
                                <SelectItem value={undefined}>All Districts</SelectItem>
                                {Object.values(District).map(district => <SelectItem key={district} value={district}>{district}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={selectedDeliveryDistrict} onValueChange={(value: District) => setSelectedDeliveryDistrict(value)}>
                            <SelectTrigger className="w-full md:w-[180px] bg-select text-foreground h-9 sm:h-10 text-sm">
                                <SelectValue placeholder="Delivery District" />
                            </SelectTrigger>
                            <SelectContent className="bg-popover text-popover-foreground">
                                <SelectItem value={undefined}>All Districts</SelectItem>
                                {Object.values(District).map(district => <SelectItem key={district} value={district}>{district}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Button variant="outline" onClick={() => { setSearchQuery(''); setSelectedJobStatus(undefined); setSelectedPickupDistrict(undefined); setSelectedDeliveryDistrict(undefined); }} className="h-9 sm:h-10 text-sm">
                            Reset Filters
                        </Button>
                    </div>

                    {/* Jobs Table */}
                    <div className="rounded-md border bg-card text-card-foreground shadow-sm overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[80px] text-primary">ID</TableHead>
                                    <TableHead className="w-[180px] text-primary">Title</TableHead>
                                    <TableHead className="w-[150px] text-primary">Client</TableHead>
                                    <TableHead className="w-[150px] text-primary">Route</TableHead>
                                    <TableHead className="w-[100px] text-primary">Status</TableHead>
                                    <TableHead className="w-[120px] text-primary">Price (MWK)</TableHead>
                                    <TableHead className="w-[120px] text-primary">Posted On</TableHead>
                                    <TableHead className="w-[80px] text-primary text-center">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    Array.from({ length: pageSize }).map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={8}>
                                                <Skeleton className="h-8 w-full" />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : jobs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                                            <p><InfoIcon className="inline-block mr-2 h-5 w-5" /> No jobs found matching your criteria.</p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    jobs.map((job) => (
                                        <TableRow key={job.id} className="text-sm">
                                            <TableCell className="font-mono text-xs">{job.id.substring(0, 8)}...</TableCell>
                                            <TableCell className="font-medium text-foreground">{job.title}</TableCell>
                                            <TableCell>
                                                <p className="font-medium text-foreground">{job.client?.profile?.fullName || 'N/A'}</p>
                                                <p className="text-muted-foreground text-xs">{job.client?.email || 'N/A'}</p>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                <div className="flex items-center gap-1">
                                                    <MapPin size={12} className="text-primary"/> {job.pickupDistrict}
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <ArrowRight size={12} className="text-muted-foreground"/>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <MapPin size={12} className="text-accent"/> {job.deliveryDistrict}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={job.status === JobStatus.ACTIVE ? "primary" : job.status === JobStatus.COMPLETED ? "success" : "secondary"}>{job.status}</Badge>
                                            </TableCell>
                                            <TableCell className="font-medium text-foreground">MWK {job.priceAmount.toLocaleString()}</TableCell>
                                            <TableCell className="text-muted-foreground text-xs">{format(new Date(job.createdAt), 'yyyy-MM-dd')}</TableCell>
                                            <TableCell className="flex justify-center items-center gap-2">
                                                <Button variant="outline" size="sm" onClick={() => handleViewClick(job.id)} disabled={isLoading}>
                                                    <Eye className="h-4 w-4" /> {/* View details */}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-between items-center pt-4">
                        <p className="text-sm text-muted-foreground">
                            Showing {Math.min(totalJobs, (page - 1) * pageSize + 1)} - {Math.min(totalJobs, page * pageSize)} of {totalJobs} jobs
                        </p>
                        <div className="flex gap-2">
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setPage(prev => Math.max(1, prev - 1))} 
                                disabled={page === 1}
                            >
                                <ChevronLeft className="h-4 w-4" /> Previous
                            </Button>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setPage(prev => prev + 1)} 
                                disabled={page >= totalPages}
                            >
                                Next <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Job Details Modal */}
            {isViewModalOpen && viewingJob && ( // Ensure modal opens with full data
                <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
                    <DialogContent className="sm:max-w-3xl bg-card text-card-foreground"> {/* Increased max-width for job details */}
                        <DialogHeader>
                            <DialogTitle className="text-foreground">Job Details: {viewingJob.title}</DialogTitle>
                            <DialogDescription>
                                Comprehensive information about this transportation job.
                            </DialogDescription>
                        </DialogHeader>
                        {isLoadingFullJob ? (
                           <div className="py-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /> <p className="text-muted-foreground mt-2">Loading job details...</p></div>
                        ) : fullJobError ? (
                           <Alert variant="destructive">
                                <Terminal className="h-4 w-4" />
                                <AlertTitle>Error Loading Job Details</AlertTitle>
                                <AlertDescription>{fullJobError.message}</AlertDescription>
                            </Alert>
                        ) : (
                            <div className="grid gap-4 py-4 max-h-[70vh] overflow-y-auto">
                                {/* General Job Info */}
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-primary">Job Overview</h4>
                                    <p className="text-sm"><strong>ID:</strong> <span className="font-mono text-xs">{viewingJob.id}</span></p>
                                    <div className="text-sm flex items-center gap-2"><strong>Status:</strong> <Badge variant={viewingJob.status === JobStatus.ACTIVE ? "primary" : viewingJob.status === JobStatus.COMPLETED ? "success" : "secondary"}>{viewingJob.status}</Badge></div>
                                    <p className="text-sm"><strong>Description:</strong> {viewingJob.description || 'N/A'}</p>
                                    <p className="text-sm"><strong>Price:</strong> MWK {viewingJob.priceAmount.toLocaleString()}</p>
                                    <p className="text-sm"><strong>Loads:</strong> {viewingJob.loadCount}</p>
                                    <p className="text-sm"><strong>Cargo Weight:</strong> {viewingJob.cargoWeight ? `${viewingJob.cargoWeight} ${viewingJob.cargoUnit || 'N/A'}` : 'N/A'} ({viewingJob.cargoWeightKg ? `${viewingJob.cargoWeightKg} kg` : 'N/A'})</p>
                                    <p className="text-sm"><strong>Special Requirements:</strong> {viewingJob.specialRequirements || 'None'}</p>
                                    {/* FIX: Use new Date() for reliable date formatting, as parseISO(null) fails */}
                                    <p className="text-sm"><strong>Posted On:</strong> {format(new Date(viewingJob.createdAt), 'yyyy-MM-dd HH:mm')}</p>
                                    {/* FIX: Ensure field exists and is a valid date before formatting */}
                                    {viewingJob.updatedAt && <p className="text-sm"><strong>Last Updated:</strong> {format(new Date(viewingJob.updatedAt), 'yyyy-MM-dd HH:mm')}</p>}
                                </div>

                                {/* Client Info */}
                                {viewingJob.client && (
                                    <div className="space-y-2 pt-4 border-t border-border">
                                        <h4 className="font-semibold text-primary">Client Information</h4>
                                        <p className="text-sm"><strong>Name:</strong> {viewingJob.client.profile?.fullName || 'N/A'}</p>
                                        <p className="text-sm"><strong>Email:</strong> {viewingJob.client.email}</p>
                                        <Button variant="link" size="sm" className="pl-0 pt-0 text-blue-500 hover:underline">
                                            <Link to={`/admin/users?search=${viewingJob.client.email}`}>View Client Profile</Link>
                                        </Button>
                                    </div>
                                )}

                                {/* Pickup Details */}
                                <div className="space-y-2 pt-4 border-t border-border">
                                    <h4 className="font-semibold text-primary">Pickup Details</h4>
                                    <p className="text-sm"><strong>District:</strong> {viewingJob.pickupDistrict}</p>
                                    <p className="text-sm"><strong>Address:</strong> {viewingJob.pickupAddress}</p>
                                    <p className="text-sm"><strong>Contact Name:</strong> {viewingJob.pickupContactName || 'N/A'}</p>
                                    <p className="text-sm"><strong>Contact Phone:</strong> {viewingJob.pickupContactPhone || 'N/A'}</p>
                                    {/* FIX: Use new Date() and check for existence */}
                                    <p className="text-sm"><strong>Preferred Date:</strong> {viewingJob.preferredPickupDate ? format(new Date(viewingJob.preferredPickupDate), 'PPP') : 'Anytime'}</p>
                                </div>

                                {/* Delivery Details */}
                                <div className="space-y-2 pt-4 border-t border-border">
                                    <h4 className="font-semibold text-primary">Delivery Details</h4>
                                    <p className="text-sm"><strong>District:</strong> {viewingJob.deliveryDistrict}</p>
                                    <p className="text-sm"><strong>Address:</strong> {viewingJob.deliveryAddress}</p>
                                    <p className="text-sm"><strong>Contact Name:</strong> {viewingJob.deliveryContactName || 'N/A'}</p>
                                    <p className="text-sm"><strong>Contact Phone:</strong> {viewingJob.deliveryContactPhone || 'N/A'}</p>
                                    {/* FIX: Use new Date() and check for existence */}
                                    <p className="text-sm"><strong>Preferred Date:</strong> {viewingJob.preferredDeliveryDate ? format(new Date(viewingJob.preferredDeliveryDate), 'PPP') : 'Anytime'}</p>
                                </div>

                                {/* Bookings */}
                                {viewingJob.bookings.length > 0 && (
                                    <div className="space-y-2 pt-4 border-t border-border">
                                        <h4 className="font-semibold text-primary">Bookings ({viewingJob.bookings.length})</h4>
                                        <ul className="text-sm list-disc pl-5">
                                            {viewingJob.bookings.map(booking => (
                                                <li key={booking.id} className="mb-1 text-muted-foreground">
                                                    <strong>Status:</strong> {booking.status} | <strong>Fee:</strong> MWK {booking.bookingFee.toLocaleString()} <br/>
                                                    <strong>Transporter:</strong> {booking.transporter?.profile?.fullName || booking.transporter?.email || 'N/A'} ({booking.vehicle?.licensePlate || 'N/A'})
                                                    <Button variant="link" size="sm" className="pl-0 pt-0 text-blue-500 hover:underline">
                                                      <Link to={`/admin/users?search=${booking.transporter?.email}`}>View Transporter</Link>
                                                    </Button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Payment Status (Simplified for view) */}
                                {viewingJob.payment && (
                                    <div className="space-y-2 pt-4 border-t border-border">
                                        <h4 className="font-semibold text-primary">Payment Status</h4>
                                        <p className="text-sm"><strong>Total Amount:</strong> MWK {viewingJob.payment.totalAmount.toLocaleString()}</p>
                                        <p className="text-sm"><strong>K&C Commission:</strong> MWK {viewingJob.payment.commissionAmount.toLocaleString()}</p>
                                        <p className="text-sm"><strong>Transporter Earnings:</strong> MWK {viewingJob.payment.transporterAmount.toLocaleString()}</p>
                                        <div className="text-sm flex items-center gap-2"><strong>Status:</strong> <Badge>{viewingJob.payment.status}</Badge></div>
                                        <p className="text-sm"><strong>PayChangu Ref:</strong> {viewingJob.payment.paychanguRef || 'N/A'}</p>
                                        {/* FIX: Use new Date() for reliable date formatting */}
                                        <p className="text-sm"><strong>Initiated:</strong> {format(new Date(viewingJob.payment.createdAt), 'yyyy-MM-dd HH:mm')}</p>
                                    </div>
                                )}

                                {viewingJob.images.length > 0 && (
                                    <div className="space-y-2 pt-4 border-t border-border">
                                        <h4 className="font-semibold text-primary">Cargo Images</h4>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {viewingJob.images.map((image, index) => (
                                                <a key={index} href={`/uploads/${image.filePath}`} target="_blank" rel="noopener noreferrer" className="block rounded-md overflow-hidden hover:opacity-90 transition-opacity">
                                                    <img src={`/uploads/${image.filePath}`} alt={`Cargo Image ${index + 1}`} className="w-full h-24 object-cover" />
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}

                            </div>
                        )}
                        <DialogFooter>
                            <Button onClick={() => setIsViewModalOpen(false)}>Close</Button>
                            {/* Add actions like "Cancel Job" here if applicable for Admin */}
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

export default AllJobsPage;